import { visibleSupportTicketReplies } from './support-ai-thread';

type PreviewTicket = Pick<Api.Support.Ticket, 'ticket_number'> & Partial<Pick<Api.Support.Ticket,
  'replies' | 'latest_visible_reply' | 'unread_state'>>;
type ActivityLabel = 'agentReplied' | 'customerReplied' | 'supportReplied' | 'youReplied' |
  'aiReplied' | 'aiDraftCreated' | 'internalNoteAdded' | 'systemReplied';

export interface SupportReplyActivity extends Api.Support.LatestVisibleReply {
  labelKey: ActivityLabel;
  author?: Api.Support.User;
}

function prosePreview(value: string): string {
  return value
    .replace(/!?(\[([^\]]*)\])\([^)]*\)/g, '$2')
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    .replace(/^\s*(?:[-*+] |\d+[.)] |>[ \t]?)/gm, '')
    .replace(/(\*\*|__|~~)([\s\S]*?)\1/g, '$2')
    .replace(/\*([^*\n]+)\*/g, '$1')
    .replace(/(^|\s)_([^_\n]+)_(?=\s|[.,!?]|$)/g, '$1$2')
    .replace(/<[^>]+>/g, ' ');
}

/** Plain text only: retain setup/code content while removing Markdown presentation. */
export function supportTicketPlainPreview(content: string, verifiedAI: boolean): string {
  const text = verifiedAI ? content.replace(/\[\[source:src_[0-9a-f]{12}\]\]/g, '') : content;
  let plain = '', cursor = 0;
  // The earliest code/hidden token owns its whole payload, including any nested fence.
  const tokens = /^[ \t]*(`{3,}|~{3,})[^\n]*(?:\n|(?![\s\S]))|(`+)([\s\S]*?)\2|<!--[\s\S]*?(?:-->|(?![\s\S]))|<(script|style|template)\b[^>]*>[\s\S]*?(?:<\/\4\s*>|(?![\s\S]))/gim;
  let token: RegExpExecArray | null;
  while ((token = tokens.exec(text))) {
    plain += prosePreview(text.slice(cursor, token.index));
    if (token[1]) {
      const closing = new RegExp(`^[ \\t]*${token[1][0]}{${token[1].length},}[ \\t]*(?:\\r?\\n|(?![\\s\\S]))`, 'gm');
      closing.lastIndex = tokens.lastIndex;
      const end = closing.exec(text);
      plain += ' ' + text.slice(tokens.lastIndex, end?.index ?? text.length) + ' ';
      tokens.lastIndex = end ? closing.lastIndex : text.length;
    } else if (token[2]) plain += token[3];
    else plain += ' ';
    cursor = tokens.lastIndex;
  }
  return (plain + prosePreview(text.slice(cursor))).replace(/\s+/g, ' ').trim();
}

function labelFor(reply: Api.Support.LatestVisibleReply, isAgent: boolean): ActivityLabel {
  if (reply.reply_type === 'internal') {
    return reply.author_type === 'bot' && (reply.ai_response_kind === 'draft' || reply.ai_response_kind === 'draft_revision')
      ? 'aiDraftCreated' : 'internalNoteAdded';
  }
  if (reply.author_type === 'bot' && (reply.ai_response_kind === 'answer' || reply.ai_response_kind === 'clarification')) return 'aiReplied';
  if (reply.author_type === 'bot' || reply.author_type === 'system') return 'systemReplied';
  if (reply.author_type === 'agent') return isAgent ? 'agentReplied' : 'supportReplied';
  return isAgent ? 'customerReplied' : 'youReplied';
}

function activity(reply: Api.Support.LatestVisibleReply, isAgent: boolean, author?: Api.Support.User, plain = false): SupportReplyActivity {
  const labelKey = labelFor(reply, isAgent);
  return {
    ...reply,
    preview: plain ? reply.preview : supportTicketPlainPreview(reply.preview, labelKey === 'aiReplied' || labelKey === 'aiDraftCreated'),
    labelKey,
    ...(author ? { author } : {})
  };
}

function fromDetail(reply: Api.Support.TicketReply, isAgent: boolean): SupportReplyActivity {
  const authorType = reply.author.author_type ?? (reply.author.support_role ? 'agent' : 'user');
  let aiKind: Api.Support.LatestVisibleReply['ai_response_kind'] = null;
  if (authorType === 'bot') {
    if (reply.reply_type === 'internal' && reply.customer_ai_draft?.response_id) aiKind = 'draft';
    else if (reply.reply_type === 'public' && reply.customer_ai?.response_id) aiKind = reply.customer_ai.response_kind;
  }
  return activity({
    id: reply.id, created_at: reply.created_at, updated_at: reply.updated_at,
    reply_type: reply.reply_type, author_type: authorType, ai_response_kind: aiKind, preview: reply.content
  }, isAgent, reply.author);
}

function compareTime(a: string, b: string): number {
  const first = Date.parse(a), second = Date.parse(b);
  const milliseconds = (Number.isFinite(first) ? first : 0) - (Number.isFinite(second) ? second : 0);
  if (milliseconds || !Number.isFinite(first) || !Number.isFinite(second)) return milliseconds;
  // Django timestamps retain microseconds; Date.parse alone loses their ordering.
  const fraction = (value: string) => (value.match(/\.(\d+)(?:Z|[+-]\d{2}:\d{2})$/)?.[1] ?? '').padEnd(9, '0');
  const aFraction = fraction(a), bFraction = fraction(b);
  return aFraction < bFraction ? -1 : aFraction > bFraction ? 1 : 0;
}

function compareActivity(a: Api.Support.LatestVisibleReply, b: Api.Support.LatestVisibleReply): number {
  if (a.id === b.id) return compareTime(a.updated_at, b.updated_at);
  return compareTime(a.created_at, b.created_at) || a.id - b.id;
}

/** One selected event owns every part of the card, including its optional author. */
export function selectLatestSupportReply(ticket: PreviewTicket, isAgent: boolean): SupportReplyActivity | null {
  let latest: SupportReplyActivity | null = null;
  for (const reply of visibleSupportTicketReplies(ticket.replies ?? [], isAgent)) {
    const candidate = fromDetail(reply, isAgent);
    if (!latest || compareActivity(candidate, latest) > 0) latest = candidate;
  }
  const projection = ticket.latest_visible_reply;
  const safeProjection = projection && (isAgent || (projection.reply_type === 'public' &&
    projection.ai_response_kind !== 'draft' && projection.ai_response_kind !== 'draft_revision'));
  if (safeProjection) {
    // The modern API already supplies plain text; parsing it again would damage code literals.
    const candidate = activity(projection, isAgent, undefined, true);
    if (!latest || compareActivity(candidate, latest) > 0) latest = candidate;
  } else if (!Object.prototype.hasOwnProperty.call(ticket, 'latest_visible_reply')) {
    // Older list APIs lack an event projection. Never resurrect this fallback after an explicit null.
    const unread = ticket.unread_state;
    if (unread?.last_reply_at && unread.last_reply_by) {
      const candidate = activity({
        id: 0, created_at: unread.last_reply_at, updated_at: unread.last_reply_at,
        reply_type: 'public', author_type: unread.last_reply_by, ai_response_kind: null,
        preview: unread.last_reply_preview ?? ''
      }, isAgent);
      if (!latest || compareActivity(candidate, latest) > 0) latest = candidate;
    }
  }
  return latest;
}

/** A late detail response cannot mark a newer list event as read. */
export function hasSupportTicketReadCoverage(existing: PreviewTicket, rendered: PreviewTicket | null, isAgent: boolean): boolean {
  if (!rendered || rendered.ticket_number !== existing.ticket_number || !Array.isArray(rendered.replies)) return false;
  const latest = selectLatestSupportReply(existing, isAgent);
  if (!latest) return true;
  const seen = selectLatestSupportReply({ ticket_number: rendered.ticket_number, replies: rendered.replies, latest_visible_reply: null }, isAgent);
  return seen !== null && compareActivity(seen, latest) >= 0;
}

/** Keep list-only evidence while importing detail data; clear only replies actually rendered. */
export function mergeSupportTicketDetail(existing: Api.Support.Ticket, detail: Api.Support.Ticket, isAgent: boolean): Api.Support.Ticket {
  const covered = hasSupportTicketReadCoverage(existing, detail, isAgent);
  const readState = covered ? detail.agent_read_state ?? existing.agent_read_state : existing.agent_read_state;
  const oldProjection = existing.latest_visible_reply, nextProjection = detail.latest_visible_reply;
  const projection = nextProjection && (!oldProjection || compareActivity(nextProjection, oldProjection) > 0)
    ? nextProjection : 'latest_visible_reply' in existing ? oldProjection : nextProjection;
  return {
    ...existing, ...detail,
    ...('latest_visible_reply' in existing || 'latest_visible_reply' in detail ? { latest_visible_reply: projection } : {}),
    replies_count: covered ? detail.replies?.length ?? existing.replies_count : existing.replies_count,
    unread_state: existing.unread_state
      ? { ...existing.unread_state, has_new_reply: covered ? false : existing.unread_state.has_new_reply }
      : existing.unread_state ?? null,
    agent_read_state: covered && readState
      ? { ...readState, seen_by_any_agent: true, viewed_by_me: true, has_updates_since_my_view: false }
      : readState
  };
}

/** A late list response controls membership, but cannot erase a reply already received. */
export function mergeSupportTicketListItem(existing: Api.Support.Ticket | undefined, incoming: Api.Support.Ticket, isAgent: boolean): Api.Support.Ticket {
  if (!existing || existing.ticket_number !== incoming.ticket_number) return incoming;
  const latest = selectLatestSupportReply(existing, isAgent);
  const next = selectLatestSupportReply(incoming, isAgent);
  const order = latest && next ? compareActivity(next, latest) : null;
  const coveredEqual = order === 0 && existing.unread_state?.has_new_reply === false &&
    hasSupportTicketReadCoverage(incoming, existing, isAgent);
  if (!latest || (order !== null && (order > 0 || (order === 0 && !coveredEqual)))) return incoming;
  // Legacy unread metadata alone is not evidence against an explicit modern empty projection.
  if (incoming.latest_visible_reply === null && latest.id === 0) return incoming;
  const { labelKey: _label, author: _author, ...projection } = latest;
  const replies = existing.replies ? visibleSupportTicketReplies(existing.replies, isAgent).map(reply => {
    if (isAgent) return reply;
    const publicReply = { ...reply };
    delete publicReply.customer_ai_agent;
    delete publicReply.customer_ai_draft;
    delete publicReply.collaboration;
    return publicReply;
  }) : undefined;
  const merged: Api.Support.Ticket = {
    ...incoming,
    ...(replies ? { replies } : {}),
    ...(latest.id > 0 ? { latest_visible_reply: projection } : {}),
    replies_count: isAgent ? existing.replies_count : replies?.length ?? incoming.replies_count,
    unread_state: existing.unread_state ? {
      last_reply_by: latest.author_type === 'user' ? 'user' : 'agent',
      last_reply_at: latest.created_at,
      last_reply_preview: latest.preview,
      has_new_reply: existing.unread_state.has_new_reply
    } : existing.unread_state,
    ...(isAgent ? { agent_read_state: existing.agent_read_state } : {})
  };
  if (!isAgent) delete merged.agent_read_state;
  return merged;
}
